import * as z from 'zod';

export const ListAvailableAlgorithmsRequest = z.object({});
export const ListAvailableAlgorithmsResponse = z.object({
  availableAlgorithms: z
    .array(
      z.object({
        config: z
          .object({
            name: z
              .string()
              .describe('Algorithm name. This value is not translatable.')
              .min(1)
              .max(50)
              .optional(),
            description: z
              .string()
              .describe(
                'Algorithm description. This describes how the algorithm works and if it has any limitations regarding site content, number of items in the catalog, site traffic, and so on. This value is not translatable.'
              )
              .min(1)
              .max(500)
              .optional(),
            additionalInfo: z
              .string()
              .describe(
                'A supplemental `description`. It can be used to help break up and organize information. You can, for example, display this information as a tooltip or as an additional section that is collapsed by default.'
              )
              .min(1)
              .max(300)
              .optional()
              .nullable(),
            algorithmType: z
              .enum(['UNSPECIFIED', 'RELATED_ITEMS', 'GLOBAL'])
              .describe(
                'Algorithms may have the following types:\n* `RELATED_ITEMS` - This type of algorithm provides recommendations based on 1 or more other provided items. For example, when an item is added to a cart, the algorithm can suggest other items frequently bought together with that item.\n* `GLOBAL` - This type of algorithm provides general recommendations based on site or project statistics. For example, bestsellers or new arrivals.'
              )
              .optional(),
            algorithmId: z
              .string()
              .describe(
                'Algorithm ID. This must be unique for a specific app but does not have to be unique across all apps on the site or in the project.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe('Algorithm configuration.')
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that provides the algorithm. This can be an app built by Wix or a 3rd-party app.\n\nSee app IDs for [apps built by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
          )
          .min(1)
          .max(36)
          .optional(),
        catalogAppIds: z.array(z.string()).min(1).max(100).optional(),
      })
    )
    .max(500)
    .optional(),
});
export const GetRecommendationRequest = z.object({
  algorithms: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'ID of the algorithm, as defined by the app that provides it.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        appId: z
          .string()
          .describe(
            'ID of the app providing the algorithm. This can be an app built by Wix or a 3rd-party app.\n\nSee app IDs for [apps built by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
          )
          .min(1)
          .max(36),
      })
    )
    .min(1)
    .max(10),
  options: z
    .object({
      items: z
        .array(
          z.object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(300)
        .optional(),
      minimumRecommendedItems: z
        .number()
        .int()
        .describe(
          'Minimum number of items an algorithm must provide for its recommendations to be returned in the response. If no algorithm provides enough items, an empty array is returned.'
        )
        .min(0)
        .max(100)
        .optional(),
    })
    .describe('Get recommendation options.')
    .optional(),
});
export const GetRecommendationResponse = z.object({
  recommendation: z
    .object({
      items: z
        .array(
          z.object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(100)
        .optional(),
      algorithm: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the algorithm, as defined by the app that provides it.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          appId: z
            .string()
            .describe(
              'ID of the app providing the algorithm. This can be an app built by Wix or a 3rd-party app.\n\nSee app IDs for [apps built by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).'
            )
            .min(1)
            .max(36)
            .optional(),
        })
        .describe('The algorithm used to provide the recommendation.')
        .optional(),
    })
    .describe(
      'The recommended items and the algorithm that provided them. Empty if no algorithm met the `minimumRecommendedItems` threshold.'
    )
    .optional(),
});
